/* ------------------------------------------------------------------
   <collection-drilldown>

   Server-rendered pill groups are indexed once, then moved between the
   two rows. No markup is generated here, so the theme editor, Liquid
   translations and the no-JS fallback all stay in charge of the HTML.

   State
     tip      data-id of the item whose children fill row 2
     selected data-id of the item whose products are showing

   Both default to '' (empty string) rather than a real menu index.
   '' deliberately matches no pill's data-id, no group's data-group-id
   other than the implicit root, and no breadcrumb ancestor - it's the
   "plain /collections/all page, nothing selected" state. That also
   means: don't reuse '0' for this, since '0' IS the first top-level
   menu item's real id.
   ------------------------------------------------------------------ */

(function () {
  if (customElements.get('collection-drilldown')) return;

  class CollectionDrilldown extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.pool = this.querySelector('[data-pool]');
      this.primaryRow = this.querySelector('[data-row="primary"]');
      this.secondaryRow = this.querySelector('[data-row="secondary"]');
      if (!this.pool || !this.primaryRow || !this.secondaryRow) return;

      this.initialised = true;
      this.useAjax = this.dataset.ajax === 'true';
      this.resultsSelector = (this.dataset.resultsSelector || '').trim();
      this.backButton = this.querySelector('[data-back]');
      this.heading = this.querySelector('[data-cdd-heading]');
      this.crumbList = this.querySelector('[data-crumb-list]');
      this.shopAllCrumb = this.querySelector('[data-shop-all-crumb]');
      this.shopAllLink = this.shopAllCrumb ? this.shopAllCrumb.querySelector('.bc-link') : null;

      // This section only ever drives another section's collection markup
      // (e.g. Horizon's <collection-component>/<results-list>). Those
      // components extend classes with #private fields for pagination and
      // filter state and expose no public method to reload them, so the
      // only safe way in is to replace the *entire* `#shopify-section-*`
      // unit they live in, never their innerHTML - see navigate() below.
      if (this.resultsSelector) {
        const target = document.querySelector(this.resultsSelector);
        this.unit = target ? target.closest('[id^="shopify-section-"]') : null;
      }

      this.groups = new Map();
      this.items = new Map();
      this.pool.querySelectorAll('[data-group-id]').forEach((group) => {
        this.groups.set(group.dataset.groupId, group);
        group.querySelectorAll('.cdd-pill').forEach((pill) => {
          if (!pill.hasAttribute('data-all')) this.items.set(pill.dataset.id, pill);
        });
      });

      // The two groups the server pre-rendered into the rows are duplicates
      // of pool nodes. Drop them so every data-id resolves to one element.
      this.primaryRow.querySelectorAll('[data-group-id]').forEach((n) => n.remove());
      this.secondaryRow.querySelectorAll('[data-group-id]').forEach((n) => n.remove());

      this.tip = this.dataset.initialTip || '';
      this.selected = this.dataset.initialSelected || this.tip;
      this.shopAllHeadingText = this.dataset.shopAllHeadingText || '';

      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);

      this.render(false);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    /* ---------- tree helpers ---------- */

    // '0-2' lives in group '0'; '0' lives in group 'root'; '' has no group
    groupOf(id) {
      const index = String(id).lastIndexOf('-');
      return index === -1 ? 'root' : String(id).slice(0, index);
    }

    // An item with children becomes the tip itself; a leaf keeps its parent
    // as the tip so its sibling row stays on screen.
    tipFor(id) {
      if (!id) return '';
      const pill = this.items.get(id);
      if (pill && pill.dataset.children) return id;
      const parent = this.groupOf(id);
      return parent === 'root' ? id : parent;
    }

    // Root-to-leaf chain of {id, title, url} ancestors for a given id, not
    // including a synthetic root node. Empty id -> empty chain.
    ancestorChain(id) {
      const chain = [];
      let current = id;
      while (current && current !== 'root') {
        const pill = this.items.get(current);
        if (!pill) break;
        chain.unshift({
          id: current,
          title: pill.dataset.title || pill.textContent.trim(),
          url: pill.getAttribute('href')
        });
        current = this.groupOf(current);
      }
      return chain;
    }

    pillIn(row, id, wantAll) {
      if (!id) return null;
      return Array.prototype.find.call(row.querySelectorAll('.cdd-pill'), (pill) => {
        return pill.dataset.id === id && pill.hasAttribute('data-all') === wantAll;
      });
    }

    /* ---------- rendering ---------- */

    place(row, groupId) {
      const group = groupId ? this.groups.get(groupId) : null;
      const current = row.querySelector('[data-group-id]');
      if (current && current !== group) this.pool.appendChild(current);
      if (group && group.parentElement !== row) row.appendChild(group);
      row.hidden = !group;
      return Boolean(group);
    }

    render(scrollActiveIntoView) {
      this.place(this.primaryRow, this.groupOf(this.tip));
      this.place(this.secondaryRow, this.groups.has(this.tip) ? this.tip : null);

      // Skipped on the very first render (connectedCallback passes false):
      // the server already rendered the correct pill as `is-active` for
      // whatever page this is. Stripping every pill's class and reapplying
      // it here anyway is redundant on load, and because pills have
      // `transition: all`, it visibly flickers the active pill (dark ->
      // white -> dark) for a moment right after the page loads. Every
      // later call is a real interaction, where a group may have just been
      // swapped into a row and genuinely needs its active state recomputed.
      if (scrollActiveIntoView) {
        this.querySelectorAll('.cdd-pill').forEach((pill) => {
          pill.classList.remove('is-active');
          pill.removeAttribute('aria-current');
        });

        const primaryActive = this.pillIn(this.primaryRow, this.tip, false);
        const secondaryActive = this.pillIn(this.secondaryRow, this.selected, this.selected === this.tip);

        [primaryActive, secondaryActive].forEach((pill) => {
          if (!pill) return;
          pill.classList.add('is-active');
          pill.setAttribute('aria-current', 'true');
          this.keepInView(pill);
        });
      }

      if (this.backButton) this.backButton.hidden = this.groupOf(this.tip) === 'root';

      // Also skipped on the initial render, for the same reason as above:
      // the server already rendered the correct heading/breadcrumb for
      // whatever page this is, including the tricky case of a collection
      // that isn't in the menu at all (Neuheiten, Bestseller, ...) - JS
      // has no way to tell that apart from the true shop-all page using
      // only tip/selected, so it must not touch either on load. Every
      // later call is a real interaction, which - unlike the initial load -
      // can only ever land on a real menu item or the true shop-all page,
      // so syncChrome's simpler logic is safe there.
      if (scrollActiveIntoView) this.syncChrome();

      this.dispatchEvent(
        new CustomEvent('drilldown:change', {
          bubbles: true,
          detail: { tip: this.tip, selected: this.selected }
        })
      );
    }

    // Keeps the heading and breadcrumb trail in sync with tip/selected.
    // Only ever called after a real interaction (see render()), at which
    // point isAll can only mean "the true shop-all page" - never an
    // out-of-menu collection, since nothing in this component links to
    // one of those.
    syncChrome() {
      const isAll = this.selected === '';

      if (this.heading) {
        this.heading.hidden = !isAll;
        if (isAll) this.heading.textContent = this.shopAllHeadingText;
      }

      if (this.shopAllCrumb) {
        if (isAll) this.shopAllCrumb.setAttribute('aria-current', 'page');
        else this.shopAllCrumb.removeAttribute('aria-current');
      }
      if (this.shopAllLink) this.shopAllLink.classList.toggle('is-current', isAll);

      if (this.crumbList) {
        this.crumbList.querySelectorAll('[data-dyn-crumb]').forEach((node) => node.remove());

        const chain = this.ancestorChain(this.selected);
        chain.forEach((step, index) => {
          const isLast = index === chain.length - 1;
          const li = document.createElement('li');
          li.className = 'bc-item';
          li.setAttribute('data-dyn-crumb', '');
          if (isLast) {
            li.setAttribute('aria-current', 'page');
            const span = document.createElement('span');
            span.className = 'bc-current';
            span.textContent = step.title;
            li.appendChild(span);
          } else {
            const a = document.createElement('a');
            a.className = 'bc-link';
            a.href = step.url;
            a.dataset.id = step.id;
            a.textContent = step.title;
            li.appendChild(a);
          }
          this.crumbList.appendChild(li);
        });
      }
    }

    keepInView(pill) {
      const row = pill.closest('[data-row]');
      if (!row || row.scrollWidth <= row.clientWidth) return;
      pill.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }

    /* ---------- interaction ---------- */

    onClick(event) {
      const pill = event.target.closest('.cdd-pill');
      if (pill && this.contains(pill)) {
        const row = pill.closest('[data-row]');
        if (!row) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();

        const id = pill.dataset.id;
        if (row.dataset.row === 'primary') {
          this.tip = id;
          this.selected = id;
        } else if (pill.hasAttribute('data-all')) {
          this.selected = id;
        } else if (pill.dataset.children) {
          // A sub-category with children is promoted into row 1 and its own
          // children take over row 2.
          this.tip = id;
          this.selected = id;
        } else {
          this.selected = id;
        }

        this.render(true);
        this.navigate(pill.getAttribute('href'), true);
        return;
      }

      const back = event.target.closest('[data-back]');
      if (back && this.contains(back)) {
        event.preventDefault();
        const parent = this.groupOf(this.tip);
        if (parent === 'root') return;
        this.tip = parent;
        this.selected = parent;
        this.render(true);
        const parentPill = this.items.get(parent);
        if (parentPill) this.navigate(parentPill.getAttribute('href'), true);
        return;
      }

      // Breadcrumb links: the static "Shop All" crumb (data-id="") and any
      // dynamic ancestor crumb (data-id="0", "0-1", ...) both jump straight
      // to that level without a full reload.
      const crumbLink = event.target.closest('.bc-link[data-id]');
      if (crumbLink && this.contains(crumbLink)) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();

        const id = crumbLink.dataset.id;
        this.tip = this.tipFor(id);
        this.selected = id;
        this.render(true);
        this.navigate(crumbLink.getAttribute('href'), true);
      }
    }

    onPopState() {
      const path = window.location.pathname;
      let match = null;
      this.items.forEach((pill, id) => {
        if (match) return;
        const href = pill.getAttribute('href') || '';
        if (href.split('?')[0] === path) match = id;
      });

      if (!match) {
        // Not a menu item - could be the true shop-all page or some other
        // out-of-menu collection (Neuheiten, Bestseller, ...), and this
        // component can't tell those apart from the URL alone. Reload so
        // the server renders the correct heading/breadcrumb state fresh,
        // rather than guessing client-side.
        window.location.reload();
        return;
      }

      this.selected = match;
      this.tip = this.tipFor(match);
      this.render(true);
      this.navigate(window.location.href, false);
    }

    /* ---------- loading ---------- */

    navigate(url, push) {
      if (!url) return;
      if (!this.useAjax || !this.unit) {
        window.location.href = url;
        return;
      }

      if (push) {
        try {
          window.history.pushState({ drilldown: this.selected }, '', url);
        } catch (error) {
          /* pushState can throw on cross-origin or file:// contexts */
        }
      }

      const token = {};
      this.fetchToken = token;

      const unitId = this.unit.id;
      const endpoint = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + unitId.replace(/^shopify-section-/, '');

      fetch(endpoint, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((text) => {
          if (this.fetchToken !== token) return;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          // The fetched fragment IS the section (Section Rendering API
          // returns the section's outer markup for a plain section_id
          // request), but fall back to searching by id in case the theme
          // wraps it differently.
          const next = doc.getElementById(unitId) || doc.querySelector('[id^="shopify-section-"]');
          if (!next) throw new Error('Matching section not found in response - template mismatch?');
          this.unit.replaceWith(next);
          this.unit = next;
        })
        .catch(() => {
          // Different template on the destination collection, network
          // failure, or a theme that structures this section differently:
          // fall back to a normal page load so the shopper never gets a
          // stale grid.
          window.location.href = url;
        });
    }
  }

  customElements.define('collection-drilldown', CollectionDrilldown);
})();
